import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useFeatureStore } from '@store'
import { featureService } from '@services/featureService'
import { Save } from 'lucide-react'
import { Modal, Button, Input, Select, Textarea, Alert } from '@components/common'
import './FeatureForm.css'

/**
 * Find a slug that doesn't already exist by appending -2, -3, ... as needed
 */
const generateUniqueSlug = async (baseSlug) => {
	let candidate = baseSlug
	let suffix = 1

	while (suffix <= 50) {
		try {
			await featureService.getBySlug(candidate)
			// Slug exists, try the next one
			suffix += 1
			candidate = `${baseSlug}-${suffix}`
		} catch (error) {
			// Not found -> available
			return candidate
		}
	}

	return `${baseSlug}-${Date.now()}`
}

/**
 * Feature Form Validation Schema
 */
const featureSchema = z.object({
	name: z
		.string()
		.min(2, 'Name must be at least 2 characters')
		.max(255, 'Name cannot exceed 255 characters'),
	slug: z
		.string()
		.min(2, 'Slug must be at least 2 characters')
		.max(255, 'Slug cannot exceed 255 characters')
		.regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
	description: z
		.string()
		.max(1000, 'Description cannot exceed 1000 characters')
		.optional()
		.or(z.literal('')),
	category: z
		.string()
		.max(100, 'Category cannot exceed 100 characters')
		.optional()
		.or(z.literal('')),
	display_order: z
		.string()
		.refine((val) => !isNaN(parseInt(val)) && parseInt(val) >= 0, {
			message: 'Display order must be a non-negative number',
		})
		.optional()
		.or(z.literal('')),
	status: z.enum(['active', 'archived'], {
		errorMap: () => ({ message: 'Status must be active or archived' }),
	}),
})

/**
 * Feature Form Component
 *
 * Universal form for creating and editing features
 */
const FeatureForm = ({ mode = 'create', onClose }) => {
	const { selectedFeature, createFeature, updateFeature, categories } = useFeatureStore()

	const [isSubmitting, setIsSubmitting] = useState(false)
	const [submitError, setSubmitError] = useState(null)

	const isEditMode = mode === 'edit'

	// Initialize form with React Hook Form + Zod
	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		setValue,
		watch,
		reset,
	} = useForm({
		resolver: zodResolver(featureSchema),
		mode: 'onChange',
		defaultValues: {
			name: '',
			slug: '',
			description: '',
			category: '',
			display_order: '',
			status: 'active',
		},
	})

	const nameValue = watch('name')
	const [checkingSlug, setCheckingSlug] = useState(false)
	const slugCheckId = useRef(0)

	// Reset form when selectedFeature changes (for edit mode)
	useEffect(() => {
		if (isEditMode && selectedFeature) {
			const formData = {
				name: selectedFeature.name || '',
				slug: selectedFeature.slug || '',
				description: selectedFeature.description || '',
				category: selectedFeature.category || '',
				display_order: selectedFeature.display_order?.toString() || '',
				status: selectedFeature.status || 'active',
			}

			reset(formData)
		}
	}, [selectedFeature, isEditMode, reset])

	// Auto-generate a unique slug from name
	useEffect(() => {
		if (isEditMode || !nameValue) {
			return
		}

		const baseSlug = nameValue
			.toLowerCase()
			.replace(/[^a-z0-9\s-]/g, '')
			.replace(/\s+/g, '-')
			.replace(/-+/g, '-')
			.trim()

		if (!baseSlug) {
			setValue('slug', '')
			return
		}

		const requestId = ++slugCheckId.current
		setCheckingSlug(true)

		const timer = setTimeout(async () => {
			const uniqueSlug = await generateUniqueSlug(baseSlug)

			// Ignore if a newer request has started since
			if (slugCheckId.current === requestId) {
				setValue('slug', uniqueSlug, { shouldValidate: true })
				setCheckingSlug(false)
			}
		}, 400)

		return () => clearTimeout(timer)
	}, [nameValue, isEditMode, setValue])

	// Handle form submission
	const onSubmit = async (data) => {
		setIsSubmitting(true)
		setSubmitError(null)

		try {
			const formData = {
				name: data.name,
				slug: data.slug,
				description: data.description || '',
				category: data.category || null,
				display_order: data.display_order ? parseInt(data.display_order) : 0,
				status: data.status,
			}

			if (isEditMode) {
				await updateFeature(selectedFeature.id, formData)
			} else {
				await createFeature(formData)
			}

			onClose()
		} catch (error) {
			console.error('Form submission error:', error)
			setSubmitError(error.message || 'Failed to save feature')
		} finally {
			setIsSubmitting(false)
		}
	}

	const modalTitle = isEditMode ? 'Edit Feature' : 'Create New Feature'

	// Get unique categories for dropdown
	const categoryOptions = [
		{ value: '', label: 'No Category' },
		...Array.from(new Set(categories))
			.filter(Boolean)
			.map((cat) => ({
				value: cat,
				label: cat.charAt(0).toUpperCase() + cat.slice(1),
			})),
	]

	const footer = (
		<>
			<Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
				Cancel
			</Button>
			<Button
				variant="primary"
				onClick={handleSubmit(onSubmit)}
				loading={isSubmitting}
				disabled={!isDirty && isEditMode}
			>
				<Save size={18} />
				<span>{isEditMode ? 'Update Feature' : 'Create Feature'}</span>
			</Button>
		</>
	)

	return (
		<Modal
			isOpen={true}
			onClose={onClose}
			title={modalTitle}
			size="large"
			footer={footer}
			closeOnEscape={false}
			closeOnBackdrop={false}
		>
			<form className="feature-form" onSubmit={handleSubmit(onSubmit)}>
				{submitError && (
					<Alert variant="error" message={submitError} dismissible onClose={() => setSubmitError(null)} />
				)}

				{/* Basic Information */}
				<div className="feature-form__section">
					<h3 className="feature-form__section-title">Basic Information</h3>

					<div className="feature-form__grid">
						<Input
							label="Feature Name"
							placeholder="e.g., AI Document Generation"
							{...register('name')}
							error={errors.name?.message}
							required
							disabled={isSubmitting}
						/>

						<Input
							label="URL Slug"
							placeholder={checkingSlug ? 'Checking availability...' : 'e.g., ai-document-generation'}
							{...register('slug')}
							error={errors.slug?.message}
							required
							disabled={isSubmitting || isEditMode || checkingSlug}
						/>
					</div>

					<div className="feature-form__full">
						<Textarea
							label="Description"
							placeholder="Describe the feature and its benefits..."
							rows={4}
							maxLength={1000}
							showCount
							{...register('description')}
							error={errors.description?.message}
							disabled={isSubmitting}
						/>
					</div>
				</div>

				{/* Organization */}
				<div className="feature-form__section">
					<h3 className="feature-form__section-title">Organization</h3>

					<div className="feature-form__grid">
						<Select
							label="Category"
							{...register('category')}
							options={categoryOptions}
							error={errors.category?.message}
							disabled={isSubmitting}
							placeholder="Select or type a category"
						/>

						<Input
							label="Display Order"
							type="number"
							min="0"
							placeholder="e.g., 1"
							{...register('display_order')}
							error={errors.display_order?.message}
							disabled={isSubmitting}
						/>

						<Select
							label="Status"
							{...register('status')}
							options={[
								{ value: 'active', label: 'Active' },
								{ value: 'archived', label: 'Archived' },
							]}
							error={errors.status?.message}
							required
							disabled={isSubmitting}
						/>
					</div>
				</div>
			</form>
		</Modal>
	)
}

export default FeatureForm

